import { ProductMetaCode } from './meta-code.enum';
import { IMetaCodeVariant } from '@services/MarppleShop/features/ProductMeta';
import { isNil } from '@fxts/core';

// 필드 추가 시 필드 추가 1, 2, 3 을 따라가세요
interface FundingMetaProps {
  target_amount: number;
  current_amount?: number;
  description?: string;
  // 필드 추가 1
}

// TODO: @kjj 경로 수정
export class FundingMeta implements IMetaCodeVariant {
  private readonly target_amount: number;
  private readonly current_amount: number;
  private readonly description: string;
  // 필드 추가 2

  constructor(props: FundingMetaProps) {
    if (!isNil(props.current_amount) && props.current_amount < 0) {
      throw new Error('Current amount cannot be negative');
    }
    if (props.target_amount <= 0) {
      throw new Error('Target amount must be positive');
    }

    this.target_amount = props.target_amount;
    this.current_amount = props.current_amount ?? 0;
    this.description = props.description?.trim() ?? '';
    // 필드 추가 3
  }

  private get props() {
    return {
      target_amount: this.target_amount,
      current_amount: this.current_amount,
      description: this.description,
      // 필드 추가 4
    };
  }

  getCode(): ProductMetaCode {
    return ProductMetaCode.FUNDING;
  }

  getVariant() {
    return 'DEFAULT';
  }

  /**
   * 현재 펀딩 금액을 반환합니다.
   */
  getCurrentAmount(): number {
    return this.current_amount;
  }

  /**
   * 목표 펀딩 금액을 반환합니다.
   */
  getTargetAmount(): number {
    return this.target_amount;
  }

  /**
   * 펀딩 소개를 반환합니다.
   */
  getDescription(): string | undefined {
    return this.description;
  }

  getTargetAmountToString(): string {
    return this.target_amount.toString();
  }

  /**
   * 펀딩 금액을 추가한 새로운 FundingMeta 인스턴스를 반환합니다.
   */
  addFunding(amount: number): FundingMeta {
    if (amount <= 0) {
      throw new Error('Funding amount must be positive');
    }
    return FundingMeta.create({
      ...this.props,
      current_amount: this.current_amount + amount,
    });
  }

  /**
   * 펀딩 금액을 차감한 새로운 FundingMeta 인스턴스를 반환합니다.
   * 차감 결과가 음수가 되면 0으로 설정됩니다.
   */
  subtractFunding(amount: number): FundingMeta {
    if (amount <= 0) {
      throw new Error('Subtract amount must be positive');
    }
    const new_amount = Math.max(0, this.current_amount - amount);
    return FundingMeta.create({
      ...this.props,
      current_amount: new_amount,
    });
  }

  /**
   * 목표 금액을 변경한 새로운 FundingMeta 인스턴스를 반환합니다.
   */
  setTarget(target_amount: number): FundingMeta {
    if (target_amount <= 0) {
      throw new Error('Target amount must be positive');
    }
    return FundingMeta.create({
      ...this.props,
      target_amount,
    });
  }

  /**
   * 펀딩 설명을 변경한 새로운 FundingMeta 인스턴스를 반환합니다.
   */
  setDescription(description: string): FundingMeta {
    return FundingMeta.create({
      ...this.props,
      description,
    });
  }

  /**
   * 펀딩 진행률을 백분율로 반환합니다.
   * 최대 100%까지 반환됩니다.
   */
  getProgress(): number {
    return Math.min(100, (this.current_amount / this.target_amount) * 100);
  }

  /**
   * 펀딩이 완료되었는지 확인합니다.
   * 현재 금액이 목표 금액 이상이면 완료로 판단합니다.
   */
  isCompleted(): boolean {
    return this.current_amount >= this.target_amount;
  }

  /**
   * 다른 FundingMeta 인스턴스와 값이 동일한지 비교합니다.
   */
  equals(other: FundingMeta): boolean {
    const keys = Object.keys(this.props) as (keyof FundingMetaProps)[];
    return keys.every((key) => this.props[key] === other.props[key]);
  }

  /**
   * FundingMeta 생성
   */
  static create(props: FundingMetaProps): FundingMeta {
    return new FundingMeta(props); // new 키워드 사용
  }
}
